/**
 * @fileoverview Custom error classes for livestream-related errors
 *
 * Provides typed error handling for livestream API operations,
 * enabling proper error differentiation and user-friendly messages.
 *
 * @module helpers/LivestreamError
 */

import type { Livestream } from '../types/Livestream'

/**
 * Base error class for livestream-related errors
 *
 * @example
 * ```typescript
 * try {
 *   await createLivestream(data)
 * } catch (err) {
 *   if (err instanceof LivestreamError) {
 *     console.error(`Livestream error [${err.code}]: ${err.message}`)
 *   }
 * }
 * ```
 */
export class LivestreamError extends Error {
  /**
   * Creates a new LivestreamError
   * @param message - Human-readable error message
   * @param code - Machine-readable error code
   * @param statusCode - Optional HTTP status code
   */
  constructor(
    message: string,
    public code: string,
    public statusCode?: number
  ) {
    super(message)
    this.name = 'LivestreamError'
  }
}

/**
 * Error thrown when a livestream is not found (404)
 *
 * @example
 * ```typescript
 * if (response.status === 404) {
 *   throw new LivestreamNotFoundError('league', gameId)
 * }
 * ```
 */
export class LivestreamNotFoundError extends LivestreamError {
  /**
   * Creates a new LivestreamNotFoundError
   * @param type - The livestream type that was not found
   * @param gameId - The game ID that was searched
   */
  constructor(type: string, gameId: string) {
    super(
      `No ${type} livestream exists for game ${gameId}`,
      'LIVESTREAM_NOT_FOUND',
      404
    )
    this.name = 'LivestreamNotFoundError'
  }
}

/**
 * Error thrown when a livestream already exists (409 Conflict)
 * Includes the existing livestream data for recovery
 *
 * @example
 * ```typescript
 * if (response.status === 409) {
 *   const { data } = await response.json()
 *   throw new LivestreamConflictError('league', data)
 * }
 * ```
 */
export class LivestreamConflictError extends LivestreamError {
  /**
   * Creates a new LivestreamConflictError
   * @param type - The livestream type that already exists
   * @param existingLivestream - The existing livestream data (if available)
   */
  constructor(type: string, public existingLivestream?: Livestream) {
    super(
      `A ${type} livestream already exists for this game`,
      'LIVESTREAM_CONFLICT',
      409
    )
    this.name = 'LivestreamConflictError'
  }
}

/**
 * Error thrown when the streaming service is unavailable (503)
 * Typically occurs when Mux is not configured or unreachable
 *
 * @example
 * ```typescript
 * if (response.status === 503) {
 *   throw new LivestreamServiceError('Mux is not configured')
 * }
 * ```
 */
export class LivestreamServiceError extends LivestreamError {
  /**
   * Creates a new LivestreamServiceError
   * @param message - Error message describing the service issue
   */
  constructor(
    message: string = 'Streaming service is temporarily unavailable'
  ) {
    super(message, 'LIVESTREAM_SERVICE_ERROR', 503)
    this.name = 'LivestreamServiceError'
  }
}

/**
 * Error thrown for authorization failures (403)
 *
 * @example
 * ```typescript
 * if (response.status === 403) {
 *   throw new LivestreamAuthorizationError('league')
 * }
 * ```
 */
export class LivestreamAuthorizationError extends LivestreamError {
  /**
   * Creates a new LivestreamAuthorizationError
   * @param type - The livestream type that was denied access
   */
  constructor(type: string) {
    super(
      `You do not have permission to manage the ${type} livestream`,
      'LIVESTREAM_UNAUTHORIZED',
      403
    )
    this.name = 'LivestreamAuthorizationError'
  }
}

/**
 * Error thrown for invalid livestream type (400)
 */
export class LivestreamInvalidTypeError extends LivestreamError {
  /**
   * Creates a new LivestreamInvalidTypeError
   * @param type - The invalid type that was provided
   */
  constructor(type: string) {
    super(
      `Invalid livestream type: ${type}. Must be 'league', 'home', or 'visitor'`,
      'LIVESTREAM_INVALID_TYPE',
      400
    )
    this.name = 'LivestreamInvalidTypeError'
  }
}

export default LivestreamError
